import { KeyValue, ViewportScroller } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { afterNextRender, computed, inject, Injectable, signal, DOCUMENT, effect, untracked } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { IAuthVerified, IPlan, ISession } from './types';
import { backend, web } from '../environments/environment';
import { ReplaySubject, filter, map, Observable, switchMap, tap, catchError, throwError, lastValueFrom, distinctUntilChanged, of } from 'rxjs';
import { ScrollDispatcher, ViewportRuler } from '@angular/cdk/scrolling';
import { Auth, User, signInWithCustomToken, signOut, idToken, getAuth } from '@angular/fire/auth';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LocationService } from './location.service';
import { Analytics, logEvent } from '@angular/fire/analytics';

const iconsLogoNamespace = [
  'cloudigo',
  'facebook',
  'google',
  'instagram',
  'linkedin',
  'tiktok',
  'tripadvisor',
  'twitter',
  'whatsapp',
  'youtube',

  'malta',
  'gozo',
  'england'
];

const iconsMenuNamespace = [
  'gluten_free',
  'lactose_free',
  'vegan',
  'vegetarian'
]

const iconsRoot = [
  'premium',
  'about-values',
  'about-mission',
  'about-service',
  'star'
];

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private http = inject(HttpClient);
  private router = inject(LocationService);
  private ga = inject(Analytics);
  private cache = inject(CacheService);

  ip?: string = undefined;
  fireAuth = inject(Auth);
  fireState$ = new ReplaySubject<User|null>();
  userSession = signal<ISession|null>(null);

  userPackage = computed(() => this.userSession()?.package_name || 'free');
  userPlans = computed(() => this.userSession()?.plans || ['global','basic','free']);
  userViewPlans = computed(() => this.userSession()?.view_plans || ['free','global','basic','premium','extra']);
  
  isFree = computed(() => this.userPlans().includes('free'));
  isPremium = computed(() => this.userPlans().includes('premium'));
  isCorporate = computed(() => this.userPlans().includes('corporate'));
  isTourist = computed(() => this.userPlans().includes('tourist') || this.userPlans().includes('traveller'));
  isYearly = computed(() => this.userPlans().includes('yearly'));
  isPrepaidYearly = computed(() => this.userSession()?.next_package && this.userSession()?.package == '5f8eb29ff7ac972c6a2165ac');

  accountType = computed(() => {
    if(this.isCorporate()){
      return 'corporate';
    }

    if(this.isPremium()){
      return 'premium';
    }

    return 'free';
  });

  constructor() { 

    // * Listen to firebase auth changes
    afterNextRender(() => {
      (idToken(getAuth()) as Observable<string|null>).subscribe(value => {
        // localStorage.setItem('logged-in', value ? 'true' : ''); //?Apparently we dont need to store it in the local storage?
        if(this.fireAuth?.currentUser){
          this.fireState$.next(this.fireAuth.currentUser);
        } else {
          this.fireState$.next(null);
        }
      });

      // * perform a profile/sync to ensure user is up to date 
      setTimeout(() => {
        this.sync();
      }, 2000);
    });

    // * Listen to auth state changes and parse token
    this.fireState$.pipe(
      takeUntilDestroyed(),
      switchMap(state => {
        if(state){
          return state.getIdTokenResult();
        } else {
          return new Promise<null>((resolve) => resolve(null));
        }
      }),
      distinctUntilChanged((previous, current) => {
        return previous !== undefined && previous?.token == current?.token;
      }),//avoid token updates from causing us to think it's a new user
      map(parsedState => {
        if(parsedState?.claims?.subject){
          // localStorage.setItem('user-token', parsedState.token);
          // document.cookie = `utoken=${parsedState.token}`;
          return JSON.parse(parsedState.claims.subject as string) as ISession;
        }
        return null;
      })
    ).subscribe(state => {
      this.userSession.set(state);
    });

    effect(() => {
      const view_plans = this.userViewPlans();
      const cached_plans = untracked(this.cache.registeredPlans);

      const uncached_plans = view_plans.filter(plan => !cached_plans.find(p => p.identifier == plan));

      if(uncached_plans.length){
        this.http.get<IPlan[]>(backend + 'plans', { params: { identifiers: uncached_plans.join(',')} }).subscribe({
          next: (plans) => {
            this.cache.registerPlans(plans);
          },
          error: (error) => {
            console.error('Failed to get plans');
          }
        });
      }
    });

  }

  async getIP(){
    if(this.ip){
      return this.ip;
    }

    return await lastValueFrom(this.http.get<{ ip: string }>('https://api.ipify.org?format=json').pipe(map(response => response.ip), tap(ip => this.ip = ip), catchError(e => of(undefined))));
  }

  sync() {
    if(this.userSession()){
      this.http.get<{ message: string }>(backend + 'profile/sync').pipe(catchError((error) => {
        if(error.token){
          return this.login(error, 'sync');
        } else {
          return throwError(() => error);
        }
      }))
      .subscribe({
        error: (error) => {
          //!sync failed
          this.logout('Failed to Sync');
          console.error('Error Syncing', error);
        }
      });
    }
  }

  async login(loginResponse: IAuthVerified, state: 'login' | 'register' | 'sync' = 'login'): Promise<void> {
    if(state != 'sync'){
      this.logout(`New State: ${state}`);
    }
    await signInWithCustomToken(getAuth(), loginResponse.token);

    if(state == 'register'){
      logEvent(this.ga, 'sign_up');
    }
    
    if(state == 'sync'){
      logEvent(this.ga, 'resync');
    } else {
      logEvent(this.ga, 'login');//both login & register
    }


    try {
      localStorage?.setItem('username', loginResponse['user'] ?? '');
    } catch (e){}

    if(state != 'sync'){
      const redirectState = sessionStorage.getItem('redirect');
      const navigation: { route: string; queryParams?: { [key: string]: string }; fragment?: string; } = { route: '/' };
      if(redirectState){
        try{
          const newState = JSON.parse(redirectState)
          Object.assign(navigation, newState);
        } catch(error){

        }
        sessionStorage.removeItem('redirect');
      }
      this.router.navigate([navigation.route], { queryParams: navigation.queryParams, fragment: navigation.fragment });
    } else if(this.router.current.includes('checkout')) {
      this.router.navigate(['/']);
    }
    
  }

  async logout(reason?: string): Promise<void> {
    if(this.fireAuth?.currentUser){
      // console.log('Logging out', reason);
      await signOut(getAuth());
      logEvent(this.ga, 'logout', { reason });

    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class ResizeService {
  document = inject(DOCUMENT);

  resizer = inject(ViewportRuler).change(300).pipe(map(() => this.document.body.getBoundingClientRect()), tap(box => this.setSize(box)));
  width = signal<number>(1920);
  height = signal<number>(1080);
  constructor(){
    afterNextRender(() => {
      this.setSize(this.document?.body.getBoundingClientRect());
    });
  }

  private setSize(box?: DOMRect){
    if(box){
      this.width.set(box.width);
      this.height.set(box.height);
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class ScrollService {
  scrollInfo = inject(ViewportScroller);
  private lastScrollPos: number = 0;
  scroller = inject(ScrollDispatcher).scrolled(50).pipe(filter(e => !e), map(() => this.scrollInfo.getScrollPosition()), filter(([x,y]) => y != this.lastScrollPos), map(([_,position]) => {
    const previousPosition = this.lastScrollPos;
    const direction : 'up' | 'down' = previousPosition < position ? 'down' : 'up';
    this.lastScrollPos = position;
    return { 
      position,
      previousPosition,
      direction
    };
  }));
}

@Injectable({
  providedIn: 'root'
})
export class CacheService {
  sanitizer = inject(DomSanitizer);
  iconRegistry = inject(MatIconRegistry);

  registeredIcons = signal<string[]>([]);
  registeredPlans = signal<IPlan[]>([]);

  constructor(){
    // * Register Icons
    this.iconRegistry.setDefaultFontSetClass('material-symbols-outlined');
    iconsLogoNamespace.forEach(icon => this.iconRegistry.addSvgIconInNamespace('logo', icon, this.sanitizer.bypassSecurityTrustResourceUrl(`${web}/icons/logos/${icon}.svg`)));
    iconsMenuNamespace.forEach(icon => this.iconRegistry.addSvgIconInNamespace('menu', icon, this.sanitizer.bypassSecurityTrustResourceUrl(`${web}/icons/menu_options/${icon}.svg`)));
    iconsRoot.forEach(icon => this.iconRegistry.addSvgIcon(icon, this.sanitizer.bypassSecurityTrustResourceUrl(`${web}/icons/${icon}.svg`)));
  }
  
  sanitizeSVG(svg: string){
    return this.sanitizer.bypassSecurityTrustHtml(svg);
  }

  registerIconFromSVG(key: string, value: string){
    if(this.registeredIcons().includes(key)) return;
    this.iconRegistry.addSvgIconLiteral(key, this.sanitizer.bypassSecurityTrustHtml(value));
    this.registeredIcons.update(icons => [...icons, key]);
  }

  registerIconsFromSVG(list: KeyValue<string,string>[]){
    const newKeys: string[] = [];

    list.forEach(item => {
      if(this.registeredIcons().includes(item.key)) return;
      this.iconRegistry.addSvgIconLiteral(item.key, this.sanitizer.bypassSecurityTrustHtml(item.value));
      newKeys.push(item.key);
    });

    if(newKeys.length){
      this.registeredIcons.update(icons => [...icons, ...newKeys]);
    }
  }

  registerPlan(plan: IPlan){
    if(!(this.registeredPlans()||[]).find(rp => rp.identifier == plan.identifier)){
      this.registeredPlans.update(plans => [...plans, plan]);
    }
  }

  registerPlans(plans: IPlan[]){
    const existingPlans = (this.registeredPlans()||[]).map(p => p.identifier);
    const newPlans = plans.filter(plan => !existingPlans.includes(plan.identifier));
    if(newPlans.length){
      this.registeredPlans.update(plans => [...plans, ...newPlans]);
    }
  }
}